import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Save, X, HardHat, Wrench } from "lucide-react";

/**
 * CategoriesManagerDialog — gestionează categorii PSA + Echipament:
 *  - PSA: list built-in + custom, permite rename label/icon (build-in & custom) și delete (doar custom)
 *  - Echipament: list custom + cele detectate automat din inventar; rename & delete (doar custom)
 *
 * Props:
 *  - onClose, onChanged
 */
export default function CategoriesManagerDialog({ onClose, onChanged }) {
  const [tab, setTab] = useState("psa");
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  // Add PSA form
  const [newPsaLabel, setNewPsaLabel] = useState("");
  const [newPsaIcon, setNewPsaIcon] = useState("📦");
  const [newPsaNeedsSize, setNewPsaNeedsSize] = useState(false);
  const [newPsaNeedsSerial, setNewPsaNeedsSerial] = useState(false);
  // Add Equipment form
  const [newEqName, setNewEqName] = useState("");
  // Edit mode
  const [editingPsa, setEditingPsa] = useState(null); // {code, label, icon, ...}
  const [editingEq, setEditingEq] = useState(null);   // {old_name, new_name}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/meta");
      setMeta(r.data);
    } catch (e) {
      toast.error("Eroare la încărcare");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addPsa = async () => {
    if (!newPsaLabel.trim()) { toast.error("Numele categoriei e obligatoriu"); return; }
    setBusy(true);
    try {
      await api.post("/inventory/psa-categories", {
        label: newPsaLabel.trim(),
        icon: newPsaIcon || "📦",
        needs_size: newPsaNeedsSize,
        needs_serial: newPsaNeedsSerial,
      });
      toast.success(`Categorie PSA „${newPsaLabel}" adăugată`);
      setNewPsaLabel(""); setNewPsaIcon("📦"); setNewPsaNeedsSize(false); setNewPsaNeedsSerial(false);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
    finally { setBusy(false); }
  };

  const savePsaEdit = async () => {
    if (!editingPsa) return;
    setBusy(true);
    try {
      await api.patch(`/inventory/psa-categories/${editingPsa.code}`, {
        label: editingPsa.label,
        icon: editingPsa.icon,
        needs_size: editingPsa.needs_size,
        needs_serial: editingPsa.needs_serial,
      });
      toast.success("Categorie actualizată");
      setEditingPsa(null);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
    finally { setBusy(false); }
  };

  const deletePsa = async (cat) => {
    if (!window.confirm(`Ștergi categoria „${cat.label}"?`)) return;
    try {
      await api.delete(`/inventory/psa-categories/${cat.code}`);
      toast.success("Categorie ștearsă");
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const addEq = async () => {
    if (!newEqName.trim()) { toast.error("Numele e obligatoriu"); return; }
    setBusy(true);
    try {
      await api.post("/inventory/equipment-categories", { name: newEqName.trim() });
      toast.success(`Categorie echipament „${newEqName}" adăugată`);
      setNewEqName("");
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
    finally { setBusy(false); }
  };

  const saveEqEdit = async () => {
    if (!editingEq) return;
    if (!editingEq.new_name.trim()) { toast.error("Noul nume e obligatoriu"); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/inventory/equipment-categories/${encodeURIComponent(editingEq.old_name)}`, {
        new_name: editingEq.new_name.trim(),
      });
      toast.success(`Redenumită · ${r.data.renamed_items} item(e) actualizate`);
      setEditingEq(null);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
    finally { setBusy(false); }
  };

  const deleteEq = async (name) => {
    if (!window.confirm(`Ștergi categoria „${name}"?`)) return;
    try {
      await api.delete(`/inventory/equipment-categories/${encodeURIComponent(name)}`);
      toast.success("Categorie ștearsă");
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const psaCats = meta?.psa_categories || [];
  const eqCats = meta?.equipment_categories || [];
  const customEq = new Set(meta?.custom_equipment_categories || []);

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="categories-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-zinc-800">
            🗂 Gestionează categorii inventar
          </DialogTitle>
          <DialogDescription className="text-xs">
            Adaugă, redenumește sau șterge categorii pentru PSA și Echipamente. Categoriile built-in pot fi doar redenumite (icoană + label), nu șterse.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2 mb-3">
            <TabsTrigger value="psa" data-testid="cats-tab-psa"><HardHat size={14} className="mr-1.5"/> PSA ({psaCats.length})</TabsTrigger>
            <TabsTrigger value="eq" data-testid="cats-tab-eq"><Wrench size={14} className="mr-1.5"/> Echipament ({eqCats.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="psa" className="overflow-y-auto flex-1 space-y-3 mt-0">
            {/* Add new PSA */}
            <div className="card-flat p-3 bg-cyan-50/40 border-l-4 border-cyan-400">
              <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-800 mb-2">Adaugă categorie PSA nouă</div>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                <Input value={newPsaIcon} onChange={(e) => setNewPsaIcon(e.target.value)} placeholder="Emoji" className="md:col-span-1 h-9 text-center text-lg" maxLength={4} data-testid="new-psa-icon"/>
                <Input value={newPsaLabel} onChange={(e) => setNewPsaLabel(e.target.value)} placeholder="Nume categorie (ex: Ochelari protecție)" className="md:col-span-3 h-9" data-testid="new-psa-label"/>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <Checkbox checked={newPsaNeedsSize} onCheckedChange={(v) => setNewPsaNeedsSize(!!v)} data-testid="new-psa-needs-size"/>
                  <span>Cu mărime</span>
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <Checkbox checked={newPsaNeedsSerial} onCheckedChange={(v) => setNewPsaNeedsSerial(!!v)} data-testid="new-psa-needs-serial"/>
                  <span>Cu serie</span>
                </label>
              </div>
              <div className="mt-2 flex justify-end">
                <Button onClick={addPsa} disabled={busy || !newPsaLabel.trim()} size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="new-psa-add">
                  <Plus size={14} className="mr-1"/>Adaugă
                </Button>
              </div>
            </div>

            {/* List PSA */}
            <div className="border rounded divide-y">
              {loading ? (
                <div className="p-4 text-center text-zinc-500 text-sm">Se încarcă...</div>
              ) : psaCats.length === 0 ? (
                <div className="p-4 text-center text-zinc-500 italic text-sm">Nicio categorie definită.</div>
              ) : psaCats.map((c) => {
                const isEditing = editingPsa?.code === c.code;
                return (
                  <div key={c.code} className="p-2.5 flex items-center gap-2 hover:bg-zinc-50" data-testid={`psa-cat-${c.code}`}>
                    {isEditing ? (
                      <>
                        <Input value={editingPsa.icon} onChange={(e) => setEditingPsa({ ...editingPsa, icon: e.target.value })} className="h-8 w-14 text-center" maxLength={4} data-testid={`psa-cat-${c.code}-icon`}/>
                        <Input value={editingPsa.label} onChange={(e) => setEditingPsa({ ...editingPsa, label: e.target.value })} className="h-8 flex-1" data-testid={`psa-cat-${c.code}-label`}/>
                        <label className="flex items-center gap-1 text-[10px] cursor-pointer">
                          <Checkbox checked={editingPsa.needs_size} onCheckedChange={(v) => setEditingPsa({ ...editingPsa, needs_size: !!v })}/>
                          Mărime
                        </label>
                        <label className="flex items-center gap-1 text-[10px] cursor-pointer">
                          <Checkbox checked={editingPsa.needs_serial} onCheckedChange={(v) => setEditingPsa({ ...editingPsa, needs_serial: !!v })}/>
                          Serie
                        </label>
                        <Button size="sm" onClick={savePsaEdit} disabled={busy} className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`psa-cat-${c.code}-save`}>
                          <Save size={12}/>
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingPsa(null)} className="h-7" data-testid={`psa-cat-${c.code}-cancel`}>
                          <X size={12}/>
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="text-xl w-9 text-center">{c.icon || "📦"}</span>
                        <span className="font-medium text-sm flex-1">{c.label}</span>
                        <span className="text-[10px] text-zinc-400 font-mono">{c.code}</span>
                        {c.needs_size && <span className="text-[9px] px-1 py-0.5 rounded bg-blue-100 text-blue-800 font-bold">MĂRIME</span>}
                        {c.needs_serial && <span className="text-[9px] px-1 py-0.5 rounded bg-purple-100 text-purple-800 font-bold">SERIE</span>}
                        {c.custom && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">CUSTOM</span>}
                        <Button size="sm" variant="outline" onClick={() => setEditingPsa({ ...c, needs_size: c.needs_size || false, needs_serial: c.needs_serial || false })} className="h-7 w-7 p-0" data-testid={`psa-cat-${c.code}-edit`}>
                          <Edit size={11}/>
                        </Button>
                        {c.custom && (
                          <Button size="sm" variant="outline" onClick={() => deletePsa(c)} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" data-testid={`psa-cat-${c.code}-delete`}>
                            <Trash2 size={11}/>
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="eq" className="overflow-y-auto flex-1 space-y-3 mt-0">
            {/* Add new Equipment */}
            <div className="card-flat p-3 bg-emerald-50/40 border-l-4 border-emerald-400">
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-2">Adaugă categorie echipament nouă</div>
              <div className="flex gap-2">
                <Input value={newEqName} onChange={(e) => setNewEqName(e.target.value)} placeholder="Nume (ex: Polizoare unghiulare, Generatoare...)" className="flex-1 h-9" data-testid="new-eq-name"/>
                <Button onClick={addEq} disabled={busy || !newEqName.trim()} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-eq-add">
                  <Plus size={14} className="mr-1"/>Adaugă
                </Button>
              </div>
            </div>

            {/* List Equipment */}
            <div className="border rounded divide-y">
              {loading ? (
                <div className="p-4 text-center text-zinc-500 text-sm">Se încarcă...</div>
              ) : eqCats.length === 0 ? (
                <div className="p-6 text-center text-zinc-500 italic text-sm">
                  Nicio categorie încă. Adaugă prima de mai sus sau lasă să fie create automat când adaugi echipamente cu câmpul „Categorie".
                </div>
              ) : eqCats.map((name) => {
                const isCustom = customEq.has(name);
                const isEditing = editingEq?.old_name === name;
                return (
                  <div key={name} className="p-2.5 flex items-center gap-2 hover:bg-zinc-50" data-testid={`eq-cat-${name}`}>
                    {isEditing ? (
                      <>
                        <Input value={editingEq.new_name} onChange={(e) => setEditingEq({ ...editingEq, new_name: e.target.value })} className="h-8 flex-1" data-testid={`eq-cat-edit-input`}/>
                        <Button size="sm" onClick={saveEqEdit} disabled={busy} className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`eq-cat-save`}>
                          <Save size={12}/>
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingEq(null)} className="h-7">
                          <X size={12}/>
                        </Button>
                      </>
                    ) : (
                      <>
                        <Wrench size={14} className="text-zinc-500"/>
                        <span className="font-medium text-sm flex-1">{name}</span>
                        {isCustom
                          ? <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">CUSTOM</span>
                          : <span className="text-[9px] px-1 py-0.5 rounded bg-zinc-200 text-zinc-700 font-bold">AUTO</span>
                        }
                        <Button size="sm" variant="outline" onClick={() => setEditingEq({ old_name: name, new_name: name })} className="h-7 w-7 p-0" data-testid={`eq-cat-${name}-edit`}>
                          <Edit size={11}/>
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deleteEq(name)} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" data-testid={`eq-cat-${name}-delete`}>
                          <Trash2 size={11}/>
                        </Button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-zinc-500 italic">
              <strong>AUTO</strong> = categorie detectată automat din items existente (ex: cineva a tastat în câmpul „Categorie" la un echipament). Redenumirea propagă schimbarea în toate items-urile asociate.
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end pt-3 border-t mt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Închide</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
